import {
	Action,
	Dispatch,
} from 'redux';
// Import services
import {
	StringService,
} from '@app/services';
// Import api models.
import {
	AllowedEndorsementDateRange,
	BuildingRequestCommercial,
	BuildingRequestCommercialExisting,
	EndorsementResponse,
	EndorsementRequestCommercial,
	EndorsementRequestPersonal,
	ErrorMessage,
	MortgageeRequest,
	PossiblePeril,
} from '@app/models/api';
// Import enumerations.
import ActionType from '@app/redux/actions/types';
import {
	BroadLineOfBusiness,
	EndorsementEntityType,
	NotificationType,
} from '@app/enumerations';
// Import controller factory.
import ControllerFactoryProvider from '@app/controllers/factory';
// Import action methods.
import {
	addNotification,
	endProcessing,
	startProcessing,
} from '@app/redux/actions/methods';
// Import action factory methods.
import {
	loadAllowedEndorsementDateRangeAction,
} from '@app/redux/actions/factories';
// Import action parameters.
import {
	BroadLineOfBusinessParameters,
	OverlayTargetParameters,
} from '@app/redux/actions/parameters';
// Import actions.
import {
	LoadAllowedEndorsementDateRangeAction
} from '@app/redux/actions';


// Endorsement Action.

interface EndorsementAction extends Action {
	endorsementId: number;
}

interface EndorsementParameters {
	endorsementId: number;
}

interface EndorsementWithOptionalDateParameters extends EndorsementParameters {
	endorsementDate?: Date;
}

interface EndorsementBuildingParameters extends EndorsementParameters {
	buildingGuid: string;
}

interface EndorsementMortgageeParameters extends EndorsementParameters {
	mortgageeGuid: string;
}

// Load Endorsement Action.

interface LoadEndorsementAction extends EndorsementAction {
	endorsement: EndorsementResponse,
}

type LoadEndorsementParameters = BroadLineOfBusinessParameters & EndorsementParameters & OverlayTargetParameters;

const loadEndorsementAction: (endorsementId: number, endorsement: EndorsementResponse) =>
	LoadEndorsementAction = (endorsementId: number, endorsement: EndorsementResponse) => ({
		endorsementId: endorsementId,
		endorsement: endorsement,
		type: ActionType.LOAD_ENDORSEMENT,
	});

const loadEndorsement = (params: LoadEndorsementParameters) =>
	async (dispatch: Dispatch): Promise<EndorsementResponse> => {
		// Generate message id.
		const messageId: string = `${StringService.getInstance().hashObject(params)}-loadEndorsement`;
		try {
			// Start processing.
			await startProcessing({
				message: 'Loading endorsement...',
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);

			// Call api to load endorsement.
			let endorsement: EndorsementResponse = params.broadLineOfBusiness === BroadLineOfBusiness.PERSONAL
				? await ControllerFactoryProvider.getControllerFactory().getEndorsementController().getEndorsementValuesPersonal(params.endorsementId)
				: await ControllerFactoryProvider.getControllerFactory().getEndorsementController().getEndorsementValuesCommercial(params.endorsementId);
			// Re-query the possible perils and deductibles.
			let endorsementPossiblePerils: PossiblePeril[] = await ControllerFactoryProvider.getControllerFactory().getEndorsementController().getPossiblePerilsAndDeductibles(params.endorsementId, undefined);

			// Load endorsement to redux.
			dispatch<LoadEndorsementAction>(loadEndorsementAction(params.endorsementId, endorsement));
			// Load the perils and deductible options to redux storage.
			dispatch<LoadEndorsementPossiblePerilsAction>(loadEndorsementPossiblePerilsAction(params.endorsementId, endorsementPossiblePerils));

			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);

			return endorsement;
		}
		catch (error: any) {
			// Add error notification.
			await addNotification({
				title: 'Error',
				primaryMessage: error.message,
				secondaryMessage: error.systemMessage,
				notificationType: NotificationType.ERROR,
			})(dispatch);
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Throw error;
			throw error;
		}
	};


interface UpdateEndorsementPersonalParameters extends EndorsementWithOptionalDateParameters, OverlayTargetParameters {
	policyInformation?: EndorsementRequestPersonal;
}

const updateEndorsementPersonal = (params: UpdateEndorsementPersonalParameters) =>
	async (dispatch: Dispatch): Promise<EndorsementResponse> => {
		// Generate message id.
		const messageId: string = `${StringService.getInstance().hashObject(params)}-updateEndorsementPersonal`;
		try {
			// Set up the process messages to be displayed.
			let messages: string[] = ['Updating endorsement...'];

			// Start processing.
			let timeoutInstances: NodeJS.Timeout[] = messages.map((message: string, index: number) => {
				return setTimeout(() => {
					startProcessing({
						message: message,
						messageId: messageId,
						overlayTarget: params.overlayTarget,
					})(dispatch);
				}, index * 3000)
			});

			// Call api to save endorsement.
			let result: boolean = await ControllerFactoryProvider.getControllerFactory().getEndorsementController().updateEndorsementPersonal(params.endorsementId, params.policyInformation, params.endorsementDate);

			// Clear out all timeout instances.
			timeoutInstances.forEach((timeoutInstance: NodeJS.Timeout) => { clearTimeout(timeoutInstance) });
			// If the result comes back false, throw an error message.
			if (!result) {
				throw new ErrorMessage(400, 'Unable to update endorsement.', 'An issue occurred while updating the endorsement.');
			}
			// Re-query the endorsement.
			let endorsement: EndorsementResponse = await ControllerFactoryProvider.getControllerFactory().getEndorsementController().getEndorsementValuesPersonal(params.endorsementId);
			// Re-query the allowed endorsement date range.
			let allowedEndorsementDateRange: AllowedEndorsementDateRange = await ControllerFactoryProvider.getControllerFactory().getEndorsementValidationController().getAllowedEndorsementDateRange(params.endorsementId);	
			// Re-query the possible perils and deductibles.
			let endorsementPossiblePerils: PossiblePeril[] = await ControllerFactoryProvider.getControllerFactory().getEndorsementController().getPossiblePerilsAndDeductibles(params.endorsementId, params.endorsementDate);

			// Load endorsement to redux.
			dispatch<LoadEndorsementAction>(loadEndorsementAction(params.endorsementId, endorsement));
			// Load allowed endorsement date range to redux.
			dispatch<LoadAllowedEndorsementDateRangeAction>(loadAllowedEndorsementDateRangeAction(allowedEndorsementDateRange, { endorsementId: params.endorsementId, overlayTarget: params.overlayTarget }));
			// Load the perils and deductible options to redux storage.
			dispatch<LoadEndorsementPossiblePerilsAction>(loadEndorsementPossiblePerilsAction(params.endorsementId, endorsementPossiblePerils));

			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);

			return endorsement;
		}
		catch (error: any) {
			// Add error notification.
			await addNotification({
				title: 'Error',
				primaryMessage: error.message,
				secondaryMessage: error.systemMessage,
				notificationType: NotificationType.ERROR,
			})(dispatch);
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Throw error;
			throw error;
		}
	};


interface UpdateEndorsementCommercialParameters extends EndorsementWithOptionalDateParameters, OverlayTargetParameters {
	policyInformation?: EndorsementRequestCommercial;
}

const updateEndorsementCommercial = (params: UpdateEndorsementCommercialParameters) =>
	async (dispatch: Dispatch): Promise<EndorsementResponse> => {
		// Generate message id.
		const messageId: string = `${StringService.getInstance().hashObject(params)}-updateEndorsementCommercial`;
		try {
			// Set up the process messages to be displayed.
			let messages: string[] = ['Updating endorsement...'];

			// Start processing.
			let timeoutInstances: NodeJS.Timeout[] = messages.map((message: string, index: number) => {
				return setTimeout(() => {
					startProcessing({
						message: message,
						messageId: messageId,
						overlayTarget: params.overlayTarget,
					})(dispatch);
				}, index * 3000)
			});

			// Call api to save endorsement.
			let result: boolean = await ControllerFactoryProvider.getControllerFactory().getEndorsementController().updateEndorsementCommercial(params.endorsementId, params.policyInformation, params.endorsementDate);

			// Clear out all timeout instances.
			timeoutInstances.forEach((timeoutInstance: NodeJS.Timeout) => { clearTimeout(timeoutInstance) });
			// If the result comes back false, throw an error message.
			if (!result) {
				throw new ErrorMessage(400, 'Unable to update endorsement.', 'An issue occurred while updating the endorsement.');
			}
			// Re-query the endorsement.
			let endorsement: EndorsementResponse = await ControllerFactoryProvider.getControllerFactory().getEndorsementController().getEndorsementValuesCommercial(params.endorsementId);
			// Re-query the allowed endorsement date range.
			let allowedEndorsementDateRange: AllowedEndorsementDateRange = await ControllerFactoryProvider.getControllerFactory().getEndorsementValidationController().getAllowedEndorsementDateRange(params.endorsementId);
			// Re-query the possible perils and deductibles.
			let endorsementPossiblePerils: PossiblePeril[] = await ControllerFactoryProvider.getControllerFactory().getEndorsementController().getPossiblePerilsAndDeductibles(params.endorsementId, params.endorsementDate);

			// Load endorsement to redux.
			dispatch<LoadEndorsementAction>(loadEndorsementAction(params.endorsementId, endorsement));
			// Load allowed endorsement date range to redux.
			dispatch<LoadAllowedEndorsementDateRangeAction>(loadAllowedEndorsementDateRangeAction(allowedEndorsementDateRange, { endorsementId: params.endorsementId, overlayTarget: params.overlayTarget }));
			// Load the possible perils and deductible options to redux storage.
			dispatch<LoadEndorsementPossiblePerilsAction>(loadEndorsementPossiblePerilsAction(params.endorsementId, endorsementPossiblePerils));

			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);

			return endorsement;
		}
		catch (error: any) {
			// Add error notification.
			await addNotification({
				title: 'Error',
				primaryMessage: error.message,
				secondaryMessage: error.systemMessage,
				notificationType: NotificationType.ERROR,
			})(dispatch);
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Throw error;
			throw error;
		}
	};

// Add Endorsement Building Action.

interface AddEndorsementBuildingParameters extends EndorsementParameters, BroadLineOfBusinessParameters, OverlayTargetParameters {
	building: BuildingRequestCommercial;
}

const addEndorsementBuilding = (params: AddEndorsementBuildingParameters) =>
	async (dispatch: Dispatch): Promise<EndorsementResponse> => {
		// Generate message id.
		const messageId: string = `${StringService.getInstance().hashObject(params)}-addEndorsementBuilding`;
		try {
			// Set up the process messages to be displayed.
			let messages: string[] = ['Saving building...'];

			// Start processing.
			let timeoutInstances: NodeJS.Timeout[] = messages.map((message: string, index: number) => {
				return setTimeout(() => {
					startProcessing({
						message: message,
						messageId: messageId,
						overlayTarget: params.overlayTarget,
					})(dispatch);
				}, index * 3000)
			});

			// Call api to add building.
			let result: string = await ControllerFactoryProvider.getControllerFactory().getEndorsementPolicyEntityController().addBuildingForEndorsement(params.endorsementId, params.building);

			// Clear out all timeout instances.
			timeoutInstances.forEach((timeoutInstance: NodeJS.Timeout) => { clearTimeout(timeoutInstance) });
			// If the result comes back false, throw an error message.
			if (!result) {
				throw new ErrorMessage(400, 'Unable to save building.', 'An issue occurred while saving the building.');
			}
			// Re-query the endorsement.
			let endorsement: EndorsementResponse = params.broadLineOfBusiness === BroadLineOfBusiness.COMMERCIAL
				? await ControllerFactoryProvider.getControllerFactory().getEndorsementController().getEndorsementValuesCommercial(params.endorsementId)
				: await ControllerFactoryProvider.getControllerFactory().getEndorsementController().getEndorsementValuesPersonal(params.endorsementId);
			// Re-query the allowed endorsement date range.
			let allowedEndorsementDateRange: AllowedEndorsementDateRange = await ControllerFactoryProvider.getControllerFactory().getEndorsementValidationController().getAllowedEndorsementDateRange(params.endorsementId);
			// Re-query the possible perils and deductibles.
			let endorsementPossiblePerils: PossiblePeril[] = await ControllerFactoryProvider.getControllerFactory().getEndorsementController().getPossiblePerilsAndDeductibles(params.endorsementId, undefined);

			// Load endorsement to redux.
			dispatch<LoadEndorsementAction>(loadEndorsementAction(params.endorsementId, endorsement));
			// Load allowed endorsement date range to redux.
			dispatch<LoadAllowedEndorsementDateRangeAction>(loadAllowedEndorsementDateRangeAction(allowedEndorsementDateRange, { endorsementId: params.endorsementId, overlayTarget: params.overlayTarget }));
			// Load the possible perils and deductible options to redux storage.
			dispatch<LoadEndorsementPossiblePerilsAction>(loadEndorsementPossiblePerilsAction(params.endorsementId, endorsementPossiblePerils));

			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);

			return endorsement;
		}
		catch (error: any) {
			// Add error notification.
			await addNotification({
				title: 'Error',
				primaryMessage: error.message,
				secondaryMessage: error.systemMessage,
				notificationType: NotificationType.ERROR,
			})(dispatch);
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Throw error;
			throw error;
		}
	};

// Update Endorsement Building Action.

interface UpdateEndorsementBuildingParameters extends EndorsementParameters, BroadLineOfBusinessParameters, OverlayTargetParameters {
	building: BuildingRequestCommercialExisting;
}

const updateEndorsementBuilding = (params: UpdateEndorsementBuildingParameters) =>
	async (dispatch: Dispatch): Promise<EndorsementResponse> => {
		// Generate message id.
		const messageId: string = `${StringService.getInstance().hashObject(params)}-updateEndorsementBuilding`;
		try {
			// Set up the process messages to be displayed.
			let messages: string[] = ['Saving building...'];

			// Start processing.
			let timeoutInstances: NodeJS.Timeout[] = messages.map((message: string, index: number) => {
				return setTimeout(() => {
					startProcessing({
						message: message,
						messageId: messageId,
						overlayTarget: params.overlayTarget,
					})(dispatch);
				}, index * 3000)
			});

			// Call api to update mortgagee.
			let result: number = await ControllerFactoryProvider.getControllerFactory().getEndorsementPolicyEntityController().updateBuildingForEndorsement(params.endorsementId, params.building);

			// Clear out all timeout instances.
			timeoutInstances.forEach((timeoutInstance: NodeJS.Timeout) => { clearTimeout(timeoutInstance) });
			// If the result comes back false, throw an error message.
			if (!result) {
				throw new ErrorMessage(400, 'Unable to save building.', 'An issue occurred while saving the building.');
			}
			// Re-query the endorsement.
			let endorsement: EndorsementResponse = params.broadLineOfBusiness === BroadLineOfBusiness.COMMERCIAL
				? await ControllerFactoryProvider.getControllerFactory().getEndorsementController().getEndorsementValuesCommercial(params.endorsementId)
				: await ControllerFactoryProvider.getControllerFactory().getEndorsementController().getEndorsementValuesPersonal(params.endorsementId);
			// Re-query the allowed endorsement date range.
			let allowedEndorsementDateRange: AllowedEndorsementDateRange = await ControllerFactoryProvider.getControllerFactory().getEndorsementValidationController().getAllowedEndorsementDateRange(params.endorsementId);
			// Re-query the possible perils and deductibles.
			let endorsementPossiblePerils: PossiblePeril[] = await ControllerFactoryProvider.getControllerFactory().getEndorsementController().getPossiblePerilsAndDeductibles(params.endorsementId, undefined);

			// Load endorsement to redux.
			dispatch<LoadEndorsementAction>(loadEndorsementAction(params.endorsementId, endorsement));
			// Load allowed endorsement date range to redux.
			dispatch<LoadAllowedEndorsementDateRangeAction>(loadAllowedEndorsementDateRangeAction(allowedEndorsementDateRange, { endorsementId: params.endorsementId, overlayTarget: params.overlayTarget }));
			// Load the possible perils and deductible options to redux storage.
			dispatch<LoadEndorsementPossiblePerilsAction>(loadEndorsementPossiblePerilsAction(params.endorsementId, endorsementPossiblePerils));

			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);

			return endorsement;
		}
		catch (error: any) {
			// Add error notification.
			await addNotification({
				title: 'Error',
				primaryMessage: error.message,
				secondaryMessage: error.systemMessage,
				notificationType: NotificationType.ERROR,
			})(dispatch);
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Throw error;
			throw error;
		}
	};

// Remove Endorsement Building Action.

type RemoveEndorsementBuildingParameters = EndorsementBuildingParameters & OverlayTargetParameters;

const removeEndorsementBuilding = (params: RemoveEndorsementBuildingParameters) =>
	async (dispatch: Dispatch): Promise<EndorsementResponse> => {
		// Generate message id.
		const messageId: string = `${StringService.getInstance().hashObject(params)}-removeEndorsementBuilding`;
		try {
			// Set up the process messages to be displayed.
			let messages: string[] = ['Removing building...'];

			// Start processing.
			let timeoutInstances: NodeJS.Timeout[] = messages.map((message: string, index: number) => {
				return setTimeout(() => {
					startProcessing({
						message: message,
						messageId: messageId,
						overlayTarget: params.overlayTarget,
					})(dispatch);
				}, index * 3000)
			});

			// Call api to remove building.
			let result: boolean = await ControllerFactoryProvider.getControllerFactory().getEndorsementPolicyEntityController().removeEntityForEndorsement(params.endorsementId, EndorsementEntityType.BUILDING, params.buildingGuid);

			// Clear out all timeout instances.
			timeoutInstances.forEach((timeoutInstance: NodeJS.Timeout) => { clearTimeout(timeoutInstance) });
			// If the result comes back false, throw an error message.
			if (!result) {
				throw new ErrorMessage(400, 'Unable to update endorsement.', 'An issue occurred while removing the building.');
			}
			// Re-query the endorsement.
			let endorsement: EndorsementResponse = await ControllerFactoryProvider.getControllerFactory().getEndorsementController().getEndorsementValuesCommercial(params.endorsementId);
			// Re-query the allowed endorsement date range.
			let allowedEndorsementDateRange: AllowedEndorsementDateRange = await ControllerFactoryProvider.getControllerFactory().getEndorsementValidationController().getAllowedEndorsementDateRange(params.endorsementId);
			// Re-query the possible perils and deductibles.
			let endorsementPossiblePerils: PossiblePeril[] = await ControllerFactoryProvider.getControllerFactory().getEndorsementController().getPossiblePerilsAndDeductibles(params.endorsementId, undefined);

			// Load endorsement to redux.
			dispatch<LoadEndorsementAction>(loadEndorsementAction(params.endorsementId, endorsement));
			// Load allowed endorsement date range to redux.
			dispatch<LoadAllowedEndorsementDateRangeAction>(loadAllowedEndorsementDateRangeAction(allowedEndorsementDateRange, { endorsementId: params.endorsementId, overlayTarget: params.overlayTarget }));
			// Load the possible perils and deductible options to redux storage.
			dispatch<LoadEndorsementPossiblePerilsAction>(loadEndorsementPossiblePerilsAction(params.endorsementId, endorsementPossiblePerils));

			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);

			return endorsement;
		}
		catch (error: any) {
			// Add error notification.
			await addNotification({
				title: 'Error',
				primaryMessage: error.message,
				secondaryMessage: error.systemMessage,
				notificationType: NotificationType.ERROR,
			})(dispatch);
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Throw error;
			throw error;
		}
	};

// Reinstate Endorsement Building Action.

type ReinstateEndorsementBuildingParameters = EndorsementBuildingParameters & OverlayTargetParameters;

const reinstateEndorsementBuilding = (params: ReinstateEndorsementBuildingParameters) =>
	async (dispatch: Dispatch): Promise<EndorsementResponse> => {
		// Generate message id.
		const messageId: string = `${StringService.getInstance().hashObject(params)}-reinstateEndorsementBuilding`;
		try {
			// Set up the process messages to be displayed.
			let messages: string[] = ['Reinstating building...'];

			// Start processing.
			let timeoutInstances: NodeJS.Timeout[] = messages.map((message: string, index: number) => {
				return setTimeout(() => {
					startProcessing({
						message: message,
						messageId: messageId,
						overlayTarget: params.overlayTarget,
					})(dispatch);
				}, index * 3000)
			});

			// Call api to reinstate building.
			let result: boolean = await ControllerFactoryProvider.getControllerFactory().getEndorsementPolicyEntityController().reinstateEntityForEndorsement(params.endorsementId, EndorsementEntityType.BUILDING, params.buildingGuid);

			// Clear out all timeout instances.
			timeoutInstances.forEach((timeoutInstance: NodeJS.Timeout) => { clearTimeout(timeoutInstance) });
			// If the result comes back false, throw an error message.
			if (!result) {
				throw new ErrorMessage(400, 'Unable to update endorsement.', 'An issue occurred while reinstating the building.');
			}
			// Re-query the endorsement.
			let endorsement: EndorsementResponse = await ControllerFactoryProvider.getControllerFactory().getEndorsementController().getEndorsementValuesCommercial(params.endorsementId);
			// Re-query the allowed endorsement date range.
			let allowedEndorsementDateRange: AllowedEndorsementDateRange = await ControllerFactoryProvider.getControllerFactory().getEndorsementValidationController().getAllowedEndorsementDateRange(params.endorsementId);
			// Re-query the possible perils and deductibles.
			let endorsementPossiblePerils: PossiblePeril[] = await ControllerFactoryProvider.getControllerFactory().getEndorsementController().getPossiblePerilsAndDeductibles(params.endorsementId, undefined);

			// Load endorsement to redux.
			dispatch<LoadEndorsementAction>(loadEndorsementAction(params.endorsementId, endorsement));
			// Load allowed endorsement date range to redux.
			dispatch<LoadAllowedEndorsementDateRangeAction>(loadAllowedEndorsementDateRangeAction(allowedEndorsementDateRange, { endorsementId: params.endorsementId, overlayTarget: params.overlayTarget }));
			// Load the possible perils and deductible options to redux storage.
			dispatch<LoadEndorsementPossiblePerilsAction>(loadEndorsementPossiblePerilsAction(params.endorsementId, endorsementPossiblePerils));

			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);

			return endorsement;
		}
		catch (error: any) {
			// Add error notification.
			await addNotification({
				title: 'Error',
				primaryMessage: error.message,
				secondaryMessage: error.systemMessage,
				notificationType: NotificationType.ERROR,
			})(dispatch);
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Throw error;
			throw error;
		}
	};

// Select Endorsement Building Action.

type SelectEndorsementBuildingParameters = EndorsementBuildingParameters;

interface SelectEndorsementBuildingAction extends EndorsementAction {
	buildingGuid: string;
}

const selectEndorsementBuildingAction: (params: SelectEndorsementBuildingParameters) =>
	SelectEndorsementBuildingAction = (params: SelectEndorsementBuildingParameters) => ({
		...params,
		type: ActionType.SELECT_ENDORSEMENT_BUILDING,
	});

const selectEndorsementBuilding = (params: SelectEndorsementBuildingParameters) =>
	async (dispatch: Dispatch): Promise<void> => {
		try {
			// Select building on redux.
			dispatch<SelectEndorsementBuildingAction>(selectEndorsementBuildingAction(params));
		}
		catch (error: any) {
			// Throw error;
			throw error;
		}
	};


// Unselect Endorsement Building Action.

type UnselectEndorsementBuildingParameters = EndorsementParameters;

type UnselectEndorsementBuildingAction = EndorsementAction;

const unselectEndorsementBuildingAction: (params: UnselectEndorsementBuildingParameters) =>
	UnselectEndorsementBuildingAction = (params: UnselectEndorsementBuildingParameters) => ({
		...params,
		type: ActionType.UNSELECT_ENDORSEMENT_BUILDING,
	});

const unselectEndorsementBuilding = (params: UnselectEndorsementBuildingParameters) =>
	async (dispatch: Dispatch): Promise<void> => {
		try {
			// Unselect building on redux.
			dispatch<UnselectEndorsementBuildingAction>(unselectEndorsementBuildingAction(params));
		}
		catch (error: any) {
			// Throw error;
			throw error;
		}
	};


// Add Endorsement Mortgagee Action.

interface AddEndorsementMortgageeParameters extends EndorsementParameters, BroadLineOfBusinessParameters, OverlayTargetParameters {
	mortgagee: MortgageeRequest;
}

const addEndorsementMortgagee = (params: AddEndorsementMortgageeParameters) =>
	async (dispatch: Dispatch): Promise<EndorsementResponse> => {
		// Generate message id.
		const messageId: string = `${StringService.getInstance().hashObject(params)}-addEndorsementMortgagee`;
		try {
			// Set up the process messages to be displayed.
			let messages: string[] = ['Saving mortgagee...'];

			// Start processing.
			let timeoutInstances: NodeJS.Timeout[] = messages.map((message: string, index: number) => {
				return setTimeout(() => {
					startProcessing({
						message: message,
						messageId: messageId,
						overlayTarget: params.overlayTarget,
					})(dispatch);
				}, index * 3000)
			});

			// Call api to add mortgagee.
			let result: string = await ControllerFactoryProvider.getControllerFactory().getEndorsementPolicyEntityController().addMortgageeForEndorsement(params.endorsementId, params.mortgagee);

			// Clear out all timeout instances.
			timeoutInstances.forEach((timeoutInstance: NodeJS.Timeout) => { clearTimeout(timeoutInstance) });
			// If the result comes back false, throw an error message.
			if (!result) {
				throw new ErrorMessage(400, 'Unable to save mortgagee.', 'An issue occurred while saving the mortgagee.');
			}
			// Re-query the endorsement.
			let endorsement: EndorsementResponse = params.broadLineOfBusiness === BroadLineOfBusiness.COMMERCIAL
				? await ControllerFactoryProvider.getControllerFactory().getEndorsementController().getEndorsementValuesCommercial(params.endorsementId)
				: await ControllerFactoryProvider.getControllerFactory().getEndorsementController().getEndorsementValuesPersonal(params.endorsementId);
			// Re-query the allowed endorsement date range.
			let allowedEndorsementDateRange: AllowedEndorsementDateRange = await ControllerFactoryProvider.getControllerFactory().getEndorsementValidationController().getAllowedEndorsementDateRange(params.endorsementId);

			// Load endorsement to redux.
			dispatch<LoadEndorsementAction>(loadEndorsementAction(params.endorsementId, endorsement));
			// Load allowed endorsement date range to redux.
			dispatch<LoadAllowedEndorsementDateRangeAction>(loadAllowedEndorsementDateRangeAction(allowedEndorsementDateRange, { endorsementId: params.endorsementId, overlayTarget: params.overlayTarget }));

			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);

			return endorsement;
		}
		catch (error: any) {
			// Add error notification.
			await addNotification({
				title: 'Error',
				primaryMessage: error.message,
				secondaryMessage: error.systemMessage,
				notificationType: NotificationType.ERROR,
			})(dispatch);
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Throw error;
			throw error;
		}
	};


// Update Endorsement Mortgagee Action.

interface UpdateEndorsementMortgageeParameters extends EndorsementMortgageeParameters, BroadLineOfBusinessParameters, OverlayTargetParameters {
	mortgagee: MortgageeRequest;
}

const updateEndorsementMortgagee = (params: UpdateEndorsementMortgageeParameters) =>
	async (dispatch: Dispatch): Promise<EndorsementResponse> => {
		// Generate message id.
		const messageId: string = `${StringService.getInstance().hashObject(params)}-updateEndorsementMortgagee`;
		try {
			// Set up the process messages to be displayed.
			let messages: string[] = ['Saving mortgagee...'];

			// Start processing.
			let timeoutInstances: NodeJS.Timeout[] = messages.map((message: string, index: number) => {
				return setTimeout(() => {
					startProcessing({
						message: message,
						messageId: messageId,
						overlayTarget: params.overlayTarget,
					})(dispatch);
				}, index * 3000)
			});

			// Call api to update mortgagee.
			let result: number = await ControllerFactoryProvider.getControllerFactory().getEndorsementPolicyEntityController().updateMortgageeForEndorsement(params.endorsementId, params.mortgageeGuid, params.mortgagee);

			// Clear out all timeout instances.
			timeoutInstances.forEach((timeoutInstance: NodeJS.Timeout) => { clearTimeout(timeoutInstance) });
			// If the result comes back false, throw an error message.
			if (!result) {
				throw new ErrorMessage(400, 'Unable to save mortgagee.', 'An issue occurred while saving the mortgagee.');
			}
			// Re-query the endorsement.
			let endorsement: EndorsementResponse = params.broadLineOfBusiness === BroadLineOfBusiness.COMMERCIAL
				? await ControllerFactoryProvider.getControllerFactory().getEndorsementController().getEndorsementValuesCommercial(params.endorsementId)
				: await ControllerFactoryProvider.getControllerFactory().getEndorsementController().getEndorsementValuesPersonal(params.endorsementId);
			// Re-query the allowed endorsement date range.
			let allowedEndorsementDateRange: AllowedEndorsementDateRange = await ControllerFactoryProvider.getControllerFactory().getEndorsementValidationController().getAllowedEndorsementDateRange(params.endorsementId);

			// Load endorsement to redux.
			dispatch<LoadEndorsementAction>(loadEndorsementAction(params.endorsementId, endorsement));
			// Load allowed endorsement date range to redux.
			dispatch<LoadAllowedEndorsementDateRangeAction>(loadAllowedEndorsementDateRangeAction(allowedEndorsementDateRange, { endorsementId: params.endorsementId, overlayTarget: params.overlayTarget }));

			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);

			return endorsement;
		}
		catch (error: any) {
			// Add error notification.
			await addNotification({
				title: 'Error',
				primaryMessage: error.message,
				secondaryMessage: error.systemMessage,
				notificationType: NotificationType.ERROR,
			})(dispatch);
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Throw error;
			throw error;
		}
	};


// Remove Endorsement Mortgagee Action.

type RemoveEndorsementMortgageeParameters = EndorsementMortgageeParameters & BroadLineOfBusinessParameters & OverlayTargetParameters;

const removeEndorsementMortgagee = (params: RemoveEndorsementMortgageeParameters) =>
	async (dispatch: Dispatch): Promise<EndorsementResponse> => {
		// Generate message id.
		const messageId: string = `${StringService.getInstance().hashObject(params)}-removeEndorsementMortgagee`;
		try {
			// Set up the process messages to be displayed.
			let messages: string[] = ['Removing mortgagee...'];

			// Start processing.
			let timeoutInstances: NodeJS.Timeout[] = messages.map((message: string, index: number) => {
				return setTimeout(() => {
					startProcessing({
						message: message,
						messageId: messageId,
						overlayTarget: params.overlayTarget,
					})(dispatch);
				}, index * 3000)
			});

			// Call api to remove mortgagee.
			let result: boolean = await ControllerFactoryProvider.getControllerFactory().getEndorsementPolicyEntityController().removeEntityForEndorsement(params.endorsementId, EndorsementEntityType.MORTGAGEE, params.mortgageeGuid);

			// Clear out all timeout instances.
			timeoutInstances.forEach((timeoutInstance: NodeJS.Timeout) => { clearTimeout(timeoutInstance) });
			// If the result comes back false, throw an error message.
			if (!result) {
				throw new ErrorMessage(400, 'Unable to update endorsement.', 'An issue occurred while removing the mortgagee.');
			}
			// Re-query the endorsement.
			let endorsement: EndorsementResponse = params.broadLineOfBusiness === BroadLineOfBusiness.COMMERCIAL
				? await ControllerFactoryProvider.getControllerFactory().getEndorsementController().getEndorsementValuesCommercial(params.endorsementId)
				: await ControllerFactoryProvider.getControllerFactory().getEndorsementController().getEndorsementValuesPersonal(params.endorsementId);
			// Re-query the allowed endorsement date range.
			let allowedEndorsementDateRange: AllowedEndorsementDateRange = await ControllerFactoryProvider.getControllerFactory().getEndorsementValidationController().getAllowedEndorsementDateRange(params.endorsementId);

			// Load endorsement to redux.
			dispatch<LoadEndorsementAction>(loadEndorsementAction(params.endorsementId, endorsement));
			// Load allowed endorsement date range to redux.
			dispatch<LoadAllowedEndorsementDateRangeAction>(loadAllowedEndorsementDateRangeAction(allowedEndorsementDateRange, { endorsementId: params.endorsementId, overlayTarget: params.overlayTarget }));

			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);

			return endorsement;
		}
		catch (error: any) {
			// Add error notification.
			await addNotification({
				title: 'Error',
				primaryMessage: error.message,
				secondaryMessage: error.systemMessage,
				notificationType: NotificationType.ERROR,
			})(dispatch);
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Throw error;
			throw error;
		}
	};


// Reinstate Endorsement Mortgagee Action.

type ReinstateEndorsementMortgageeParameters = EndorsementMortgageeParameters & BroadLineOfBusinessParameters & OverlayTargetParameters;

const reinstateEndorsementMortgagee = (params: ReinstateEndorsementMortgageeParameters) =>
	async (dispatch: Dispatch): Promise<EndorsementResponse> => {
		// Generate message id.
		const messageId: string = `${StringService.getInstance().hashObject(params)}-reinstateEndorsementMortgagee`;
		try {
			// Set up the process messages to be displayed.
			let messages: string[] = ['Reinstating mortgagee...'];

			// Start processing.
			let timeoutInstances: NodeJS.Timeout[] = messages.map((message: string, index: number) => {
				return setTimeout(() => {
					startProcessing({
						message: message,
						messageId: messageId,
						overlayTarget: params.overlayTarget,
					})(dispatch);
				}, index * 3000)
			});

			// Call api to reinstate mortgagee.
			let result: boolean = await ControllerFactoryProvider.getControllerFactory().getEndorsementPolicyEntityController().reinstateEntityForEndorsement(params.endorsementId, EndorsementEntityType.MORTGAGEE, params.mortgageeGuid);

			// Clear out all timeout instances.
			timeoutInstances.forEach((timeoutInstance: NodeJS.Timeout) => { clearTimeout(timeoutInstance) });
			// If the result comes back false, throw an error message.
			if (!result) {
				throw new ErrorMessage(400, 'Unable to update endorsement.', 'An issue occurred while reinstating the mortgagee.');
			}
			// Re-query the endorsement.
			let endorsement: EndorsementResponse = params.broadLineOfBusiness === BroadLineOfBusiness.COMMERCIAL
				? await ControllerFactoryProvider.getControllerFactory().getEndorsementController().getEndorsementValuesCommercial(params.endorsementId)
				: await ControllerFactoryProvider.getControllerFactory().getEndorsementController().getEndorsementValuesPersonal(params.endorsementId);
			// Re-query the allowed endorsement date range.
			let allowedEndorsementDateRange: AllowedEndorsementDateRange = await ControllerFactoryProvider.getControllerFactory().getEndorsementValidationController().getAllowedEndorsementDateRange(params.endorsementId);

			// Load endorsement to redux.
			dispatch<LoadEndorsementAction>(loadEndorsementAction(params.endorsementId, endorsement));
			// Load allowed endorsement date range to redux.
			dispatch<LoadAllowedEndorsementDateRangeAction>(loadAllowedEndorsementDateRangeAction(allowedEndorsementDateRange, { endorsementId: params.endorsementId, overlayTarget: params.overlayTarget }));

			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);

			return endorsement;
		}
		catch (error: any) {
			// Add error notification.
			await addNotification({
				title: 'Error',
				primaryMessage: error.message,
				secondaryMessage: error.systemMessage,
				notificationType: NotificationType.ERROR,
			})(dispatch);
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Throw error;
			throw error;
		}
	};


// Select Endorsement Mortgagee Action.

type SelectEndorsementMortgageeParameters = EndorsementMortgageeParameters;

interface SelectEndorsementMortgageeAction extends EndorsementAction {
	mortgageeGuid: string;
}

const selectEndorsementMortgageeAction: (params: SelectEndorsementMortgageeParameters) =>
	SelectEndorsementMortgageeAction = (params: SelectEndorsementMortgageeParameters) => ({
		...params,
		type: ActionType.SELECT_ENDORSEMENT_MORTGAGEE,
	});

const selectEndorsementMortgagee = (params: SelectEndorsementMortgageeParameters) =>
	async (dispatch: Dispatch): Promise<void> => {
		try {
			// Select mortgagee on redux.
			dispatch<SelectEndorsementMortgageeAction>(selectEndorsementMortgageeAction(params));
		}
		catch (error: any) {
			// Throw error;
			throw error;
		}
	};


// Unselect Endorsement Mortgagee Action.

type UnselectEndorsementMortgageeParameters = EndorsementParameters;

type UnselectEndorsementMortgageeAction = EndorsementAction;

const unselectEndorsementMortgageeAction: (params: UnselectEndorsementMortgageeParameters) =>
	UnselectEndorsementMortgageeAction = (params: UnselectEndorsementMortgageeParameters) => ({
		...params,
		type: ActionType.UNSELECT_ENDORSEMENT_MORTGAGEE,
	});

const unselectEndorsementMortgagee = (params: UnselectEndorsementMortgageeParameters) =>
	async (dispatch: Dispatch): Promise<void> => {
		try {
			// Unselect mortgagee on redux.
			dispatch<UnselectEndorsementMortgageeAction>(unselectEndorsementMortgageeAction(params));
		}
		catch (error: any) {
			// Throw error;
			throw error;
		}
	};


// Load Endorsement Possible Perils Action.

interface LoadEndorsementPossiblePerilsAction extends EndorsementAction {
	endorsementPossiblePerils: PossiblePeril[],
}

type LoadEndorsementPossiblePerilsParameters = EndorsementWithOptionalDateParameters & OverlayTargetParameters;

const loadEndorsementPossiblePerilsAction: (endorsementId: number, endorsementPossiblePerils: PossiblePeril[]) =>
	LoadEndorsementPossiblePerilsAction = (endorsementId: number, endorsementPossiblePerils: PossiblePeril[]) => ({
		endorsementId,
		endorsementPossiblePerils,
		type: ActionType.LOAD_ENDORSEMENT_POSSIBLE_PERILS,
	});

const loadEndorsementPossiblePerils = (params: LoadEndorsementPossiblePerilsParameters) =>
	async (dispatch: Dispatch): Promise<PossiblePeril[]> => {
		// Generate message id.
		const messageId: string = `${StringService.getInstance().hashObject(params)}-loadEndorsementPossiblePerils`;
		try {
			// Start processing.
			await startProcessing({
				message: 'Loading possible perils for endorsement...',
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);

			// Call api to load endorsement possible perils.
			let endorsementPossiblePerils: PossiblePeril[] = await ControllerFactoryProvider.getControllerFactory().getEndorsementController().getPossiblePerilsAndDeductibles(params.endorsementId, params.endorsementDate);

			// Load endorsement possible perils to redux.
			dispatch<LoadEndorsementPossiblePerilsAction>(loadEndorsementPossiblePerilsAction(params.endorsementId, endorsementPossiblePerils));
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);

			return endorsementPossiblePerils;
		}
		catch (error: any) {
			// Add error notification.
			await addNotification({
				title: 'Error',
				primaryMessage: error.message,
				secondaryMessage: error.systemMessage,
				notificationType: NotificationType.ERROR,
			})(dispatch);
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Throw error;
			throw error;
		}
	};


// Calculate Endorsement Action.

interface CalculateEndorsementParameters extends EndorsementParameters, BroadLineOfBusinessParameters, OverlayTargetParameters {

}

const calculateEndorsement = (params: CalculateEndorsementParameters) =>
	async (dispatch: Dispatch): Promise<EndorsementResponse> => {
		// Generate message id.
		const messageId: string = `${StringService.getInstance().hashObject(params)}-calculateEndorsement`;
		try {
			// Set up the process messages to be displayed.
			let messages: string[] = ['Calculating endorsement...'];

			// Start processing.
			let timeoutInstances: NodeJS.Timeout[] = messages.map((message: string, index: number) => {
				return setTimeout(() => {
					startProcessing({
						message: message,
						messageId: messageId,
						overlayTarget: params.overlayTarget,
					})(dispatch);
				}, index * 3000)
			});

			// Call api to add building.
			await ControllerFactoryProvider.getControllerFactory().getEndorsementController().calculateEndorsement(params.endorsementId);

			// Clear out all timeout instances.
			timeoutInstances.forEach((timeoutInstance: NodeJS.Timeout) => { clearTimeout(timeoutInstance) });

			// Re-query the endorsement.
			let endorsement: EndorsementResponse = params.broadLineOfBusiness === BroadLineOfBusiness.COMMERCIAL
				? await ControllerFactoryProvider.getControllerFactory().getEndorsementController().getEndorsementValuesCommercial(params.endorsementId)
				: await ControllerFactoryProvider.getControllerFactory().getEndorsementController().getEndorsementValuesPersonal(params.endorsementId);
			// Re-query the allowed endorsement date range.
			let allowedEndorsementDateRange: AllowedEndorsementDateRange = await ControllerFactoryProvider.getControllerFactory().getEndorsementValidationController().getAllowedEndorsementDateRange(params.endorsementId);
			// Re-query the possible perils and deductibles.
			let endorsementPossiblePerils: PossiblePeril[] = await ControllerFactoryProvider.getControllerFactory().getEndorsementController().getPossiblePerilsAndDeductibles(params.endorsementId, undefined);

			// Load endorsement to redux.
			dispatch<LoadEndorsementAction>(loadEndorsementAction(params.endorsementId, endorsement));
			// Load allowed endorsement date range to redux.
			dispatch<LoadAllowedEndorsementDateRangeAction>(loadAllowedEndorsementDateRangeAction(allowedEndorsementDateRange, { endorsementId: params.endorsementId, overlayTarget: params.overlayTarget }));
			// Load the possible perils and deductible options to redux storage.
			dispatch<LoadEndorsementPossiblePerilsAction>(loadEndorsementPossiblePerilsAction(params.endorsementId, endorsementPossiblePerils));

			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);

			return endorsement;
		}
		catch (error: any) {
			// Add error notification.
			await addNotification({
				title: 'Error',
				primaryMessage: error.message,
				secondaryMessage: error.systemMessage,
				notificationType: NotificationType.ERROR,
			})(dispatch);
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Throw error;
			throw error;
		}
	};

// Export actions.
export {
	EndorsementAction,
	LoadEndorsementAction,
	SelectEndorsementBuildingAction,
	UnselectEndorsementBuildingAction,
	SelectEndorsementMortgageeAction,
	UnselectEndorsementMortgageeAction,
	LoadEndorsementPossiblePerilsAction,
};

// Export action methods.
export {
	loadEndorsement,
	updateEndorsementPersonal,
	updateEndorsementCommercial,
	addEndorsementBuilding,
	updateEndorsementBuilding,
	removeEndorsementBuilding,
	reinstateEndorsementBuilding,
	selectEndorsementBuilding,
	unselectEndorsementBuilding,
	addEndorsementMortgagee,
	updateEndorsementMortgagee,
	removeEndorsementMortgagee,
	reinstateEndorsementMortgagee,
	selectEndorsementMortgagee,
	unselectEndorsementMortgagee,
	loadEndorsementPossiblePerils
};

// Export action parameters.
export {
	EndorsementParameters,
	EndorsementWithOptionalDateParameters,
	LoadEndorsementParameters,
	UpdateEndorsementPersonalParameters,
	UpdateEndorsementCommercialParameters,
	AddEndorsementBuildingParameters,
	UpdateEndorsementBuildingParameters,
	RemoveEndorsementBuildingParameters,
	ReinstateEndorsementBuildingParameters,
	SelectEndorsementBuildingParameters,
	UnselectEndorsementBuildingParameters,
	AddEndorsementMortgageeParameters,
	UpdateEndorsementMortgageeParameters,
	RemoveEndorsementMortgageeParameters,
	ReinstateEndorsementMortgageeParameters,
	SelectEndorsementMortgageeParameters,
	UnselectEndorsementMortgageeParameters,
	LoadEndorsementPossiblePerilsParameters,
	CalculateEndorsementParameters,
};

// Export action factories.
export {
	loadEndorsementAction,
	selectEndorsementBuildingAction,
	unselectEndorsementBuildingAction,
	selectEndorsementMortgageeAction,
	unselectEndorsementMortgageeAction,
	loadEndorsementPossiblePerilsAction,
	calculateEndorsement,
};